// api.ts

// ------------------------------------
// Imports
// ------------------------------------
import { 
    BaseCoral, 
    ClientCoral, 
    FilteredClientCoral,
    CoralType, 
    Tag, 
 
    CoralSimulationResult, 
    User, 
    ArtistApplication, 
    BlendedArtistListItem, 
    UserIdentityVerification, 
    UserCoralConnection, 
    ProcessedCoralPayments, 
    ArtistSearchResult, 
    ArtistAnalytics, 
    PaymentSummary,
    RefundRequest,
    CoralPaymentsSummary,
    MemberRefundRequestStatus,
    RefundPaymentMethod,
    PayPalDetails,
    BankTransferDetails,
    NotificationEntity
  } from 'shared/types/platformTypes';
  import {
    CoralContentSummary,
    SignalRecord,
    CommentRecord,
    CommentFeed,
    EntityType,
    ReactionRecord,
    ContentType,
    ContentFeed,
    ContentItem,
    MusicLinkResolveRequest,
    MusicLinkResolveResponse,
    OriginLocation
  } from 'shared/types/contentTypes';
  import { getCognitoTokens } from '../utils/authUtils';
  import {
    CommunityDisplayData,
    Community,
    CommunityMember,
    CommunityArtist,
    CommunityDiscussionsSnapshot,
    DashboardCardData,
    PaginatedResponse
  } from '../types/communityTypes';
  
  // ------------------------------------
  // Constants
  // ------------------------------------
  const apiEndpoint = process.env.REACT_APP_API_GATEWAY_ENDPOINT;
  const S3_BUCKET_URL = `https://wearecoral-public-data-${
    process.env.REACT_APP_ENVIRONMENT || 'dev'
  }.s3.ap-southeast-2.amazonaws.com`;
  const localApiEndpoint = 'http://localhost:3001';
  const CORAL_OPERATIONS = '/coral/operations';
  const CORAL_PUBLIC_OPERATIONS = '/coral/operations/public';
  const localApiEnabled = false;
  
  // ------------------------------------
  // Interfaces
  // ------------------------------------
  export interface ArtistSearchResponse {
    artists: ArtistSearchResult[];
    lastEvaluatedKey?: string;
  }

export interface GlobalSearchResult {
  id: string;
  type: 'person' | 'artist';
  name: string;
  displayName?: string;
  imageUrl?: string;
  subtitle?: string;
}

export interface GlobalSearchResponse {
  results: GlobalSearchResult[];
  totalCount: number;
}
  export interface UpdateCoralVisibilityResponse {
    message: string;
    sharedCoralGuid: string;
    isShared: boolean;
    url: string;
  }
  
  export interface UpdateCoralCuratedResponse {
    message: string;
    isCurated: boolean;
  }
  
  export interface SimulateCoralResponse {
    transactionFee: number;
    platformFee: number;
    artistAllocations: CoralSimulationResult[];
  }
  
  export interface VerifyArtistRequest {
    verificationUrl: string;
    verificationCode: string;
    email: string;
    paypalEmail: string;
  }
  
  // Comment-related interfaces
  export interface CommentContextInfo {
    contextEntityType?: EntityType;
    contextEntityId?: string;
    contextEntityName?: string;
    contextPrefix?: string;
  }

  export interface CommentWithContext extends CommentRecord {
    contextInfo?: CommentContextInfo;
  }

  export interface GetCommentsRequest {
    contentId: string;
    lastEvaluatedKey?: string;
    limit?: number;
    entityType?: EntityType;
    entityId?: string;
    parentCommentId?: string;
    includeReplies?: boolean; // If true, include all comments including replies (not just top-level)
  }

  export interface CreateCommentRequest {
    contentId: string;
    text: string;
    parentCommentId?: string;
    entityType?: EntityType;
    entityId?: string;
    mentions?: Array<{ userId: string; displayName: string; startIndex: number; endIndex: number }>;
  }

  export interface UpdateCommentRequest {
    contentId: string;
    commentId: string;
    text: string;
  }

  export interface DeleteCommentRequest {
    contentId: string;
    commentId: string;
  }

  export interface ReactionRequest {
    contentId: string;
    commentId: string;
    reactionType: 'resonate';
    action: 'add' | 'remove';
  }
  
  export interface CreateContentItemRequest {
    contentType: ContentType;
    title?: string;
    text: string;
    entityType?: EntityType;
    entityId?: string;
    relatedData?: Record<string, unknown>;
    mediaInfo?: Record<string, unknown>;
    originLocation?: OriginLocation;
  }

  export interface ContentItemResponse {
    contentItem: {
      contentId: string;
      contentType: ContentType;
      title?: string;
      text: string;
      createdAt: string;
      updatedAt: string;
      entityType?: EntityType;
      entityId?: string;
    };
  }

  // ------------------------------------
  // Notification Interfaces
  // ------------------------------------
  export interface GetNotificationsRequest {
    limit?: number;
    lastKey?: string;
    unreadOnly?: boolean;
  }

  export interface GetNotificationsResponse {
    notifications: NotificationEntity[];
    pagination: {
      hasMore: boolean;
      lastKey?: string;
      totalCount?: number;
    };
    unreadCount: number;
    lastViewedAt?: string;
  }

  export interface MarkNotificationsViewedRequest {
    viewedAt?: string;
  }

  export interface MarkNotificationsViewedResponse {
    success: boolean;
    lastNotificationViewedAt: string;
    message: string;
  }
  
  // ------------------------------------
  // Utility: Fetch from S3
  // ------------------------------------
  async function fetchFromS3<T>(key: string): Promise<T | null> {
    try {
      const response = await fetch(`${S3_BUCKET_URL}/${key}`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching from S3:', error);
      return null;
    }
  }
  
  // ------------------------------------
  // Generic API Calls
  // ------------------------------------

  /**
   * Stale-client guard for the CF-limit route-consolidation deploy window.
   *
   * When the backend deploys a batch that folds routes, an already-loaded
   * bundle may keep calling a route that no longer exists. API Gateway answers
   * an unknown route with 403 "Missing Authentication Token"; a consolidated
   * handler answers an unknown action with 400 "Invalid action". Either means
   * THIS client is stale — pull the new bundle with a one-time reload (guarded
   * against reload loops) rather than showing a broken screen, and emit a
   * `coral:stale-client` event for any UI that wants to show a banner instead.
   *
   * Caveat: a truly-removed route's 403 has no CORS headers, so the browser may
   * surface it as a network error rather than a readable response — off-peak
   * deploys remain the primary mitigation. Our own handlers never emit these
   * exact strings, so legitimate 400/403s (auth, validation) won't trigger it.
   */
  const STALE_RELOAD_GUARD_KEY = 'coral:staleReloadDone';

  function maybeHandleStaleClient(status: number, bodyText: string): void {
    const isUnknownRoute = status === 403 && bodyText.includes('Missing Authentication Token');
    const isUnknownAction = status === 400 && bodyText.includes('Invalid action');
    if ((!isUnknownRoute && !isUnknownAction) || typeof window === 'undefined') {
      return;
    }
    window.dispatchEvent(new CustomEvent('coral:stale-client'));
    try {
      if (!window.sessionStorage.getItem(STALE_RELOAD_GUARD_KEY)) {
        window.sessionStorage.setItem(STALE_RELOAD_GUARD_KEY, '1');
        window.location.reload();
      }
    } catch {
      // sessionStorage unavailable (e.g. private mode) — the event dispatch is the fallback.
    }
  }

  /**
   * Re-arm the stale-client reload guard once a fresh bundle confirms it works
   * (any successful authenticated response). This makes the reload one-shot per
   * stale EPISODE rather than once per browser session, so a later consolidation
   * batch that removes more routes can still trigger a recovery reload.
   */
  function clearStaleClientGuard(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.sessionStorage.removeItem(STALE_RELOAD_GUARD_KEY);
    } catch {
      // sessionStorage unavailable — nothing to clear.
    }
  }

/**
 * A non-2xx API response, carrying the status and the parsed body. Handlers answer a refused or
 * partially-applied request with detail the operator needs to act (which records, a correlation id); an
 * error that discards the body throws that away and leaves the UI guessing.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, bodyText: string) {
    super('Network response was not ok');
    this.name = 'ApiError';
    this.status = status;
    try {
      this.body = JSON.parse(bodyText);
    } catch {
      this.body = bodyText;
    }
  }
}

  async function fetchFromAPI<T>(
    endpoint: string,
    processor: (data: T) => T,
    method: 'GET' | 'POST' | 'DELETE' = 'GET',
    body?: string,
    useLocal = true // This forces it to use the local environment rather than the cloud API
  ): Promise<T> {
    const tokens = await getCognitoTokens();
    if (!tokens) {
      throw new Error('Could not retrieve Cognito idToken');
    }
    const idTokenString = tokens.idToken;
  
    const response = await fetch(
      `${useLocal && localApiEnabled ? localApiEndpoint : apiEndpoint}${endpoint}`,
      {
        method,
        headers: {
          Authorization: `Bearer ${idTokenString}`,
          'Content-Type': 'application/json',
        },
        body,
      }
    );
  
    if (!response.ok) {
      const bodyText = await response.text().catch(() => '');
      maybeHandleStaleClient(response.status, bodyText);
      throw new ApiError(response.status, bodyText);
    }

    clearStaleClientGuard();
    const data = await response.json();
    if (isType<T>(data)) {
      return processor(data);
    } else {
      throw new Error('Invalid data type');
    }
  }
  
  export async function fetchFromAPIWithoutAuth<T>(
    endpoint: string,
    processor: (data: T) => T,
    method: 'GET' | 'POST' | 'DELETE' = 'GET',
    body?: string,
    useLocal = true
  ): Promise<T> {
    const apiEndpoint = process.env.REACT_APP_API_GATEWAY_ENDPOINT;
    const response = await fetch(
      `${useLocal && localApiEnabled ? localApiEndpoint : apiEndpoint}${endpoint}`,
      {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }
    );
  
    if (!response.ok) {
      const bodyText = await response.text().catch(() => '');
      maybeHandleStaleClient(response.status, bodyText);
      throw new ApiError(response.status, bodyText);
    }

    clearStaleClientGuard();
    const data = await response.json();
    if (isType<T>(data)) {
      return processor(data);
    } else {
      throw new Error('Invalid data type');
    }
  }
  
  function isType<T>(data: unknown): data is T {
    // Basic type check; can be expanded if needed
    return typeof data === 'object' && data !== null;
  }
  
  // ------------------------------------
  // Artist-Related Functions
  // ------------------------------------
  export async function getArtistDetails(artistName: string): Promise<BlendedArtistListItem[]> {
    const endpoint = `/artist/operations`;
    const body = JSON.stringify({ action: 'get', artistName });
    return fetchFromAPI<BlendedArtistListItem[]>(
      endpoint, 
      (data) => data as BlendedArtistListItem[], 
      'POST', 
      body, 
      false
    );
  }
  
  export async function getArtistDetailsByGuid(artistGuid: string): Promise<BlendedArtistListItem[]> {
    const endpoint = `/artist/operations`;
    const body = JSON.stringify({ action: 'get', artistId: artistGuid });
    return fetchFromAPI<BlendedArtistListItem[]>(
      endpoint, 
      (data) => data as BlendedArtistListItem[], 
      'POST', 
      body, 
      false
    );
  }
  
  export async function getArtistDetailsPublic(
    artistUrl?: string, 
    artistName?: string, 
    artistId?: string, 
    cache?: boolean
  ): Promise<BlendedArtistListItem[]> {
    let s3Key: string | null = null;
  
    if (artistUrl) {
      s3Key = `artist-cache/${encodeURIComponent(artistUrl)}.json`;
    }
  
    if (s3Key) {
      const s3Data = await fetchFromS3<BlendedArtistListItem>(s3Key);
      if (s3Data) {
        return [s3Data];
      }
    }
  
    // If not in S3 or no valid key, fall back to API call
    if (!artistUrl && !artistName && !artistId) {
      throw new Error('Either artistUrl, artistName, or artistId must be provided');
    }

    const endpoint = `/artist/operations/public`;
    const body: {
      action: string;
      artistUrl?: string;
      artistName?: string;
      artistId?: string;
      cache?: boolean;
    } = { action: 'getPublic' };
    
    if (artistUrl) {
      body.artistUrl = artistUrl;
    } else if (artistName) {
      body.artistName = artistName;
    } else if (artistId) {
      body.artistId = artistId;
    }
    
    if (cache) {
      body.cache = true;
    }
  
    return fetchFromAPIWithoutAuth<BlendedArtistListItem[]>(
      endpoint, 
      (data) => data as BlendedArtistListItem[], 
      'POST', 
      JSON.stringify(body), 
      false
    );
  }
  
  export async function addArtistToMailingList(
    artistName: string,
    email: string
  ): Promise<{ message: string }> {
    const endpoint = `/artist/mailinglist`;
    const body = JSON.stringify({ artistName, email });
    return fetchFromAPIWithoutAuth<{ message: string }>(
      endpoint,
      (data) => data as { message: string },
      'POST',
      body,
      false
    );
  }

  export async function notifyArtistNotFound(
    artistName: string,
    email?: string
  ): Promise<{ message: string }> {
    const endpoint = `/artist/mailinglist`;
    const body = JSON.stringify({ artistName, email, artistNotFound: true });
    return fetchFromAPIWithoutAuth<{ message: string }>(
      endpoint,
      (data) => data as { message: string },
      'POST',
      body,
      false
    );
  }

  // Retained intentionally: the "view all contributions" popup that called this
  // was replaced by a direct link to /trust, so this currently has no caller.
  // Kept because the newsletter-list backend it feeds is the infrastructure the
  // monthly "radar" digest (the committed follow-on) will reuse.
  export async function addToNewsletterList(
    email: string,
    firstName?: string,
    lastName?: string
  ): Promise<{ message: string }> {
    const endpoint = `/user/newsletter`;
    const body = JSON.stringify({ email, firstName, lastName });
    return fetchFromAPIWithoutAuth<{ message: string }>(
      endpoint,
      (data) => data as { message: string },
      'POST',
      body,
      false
    );
  }

  export async function verifyArtist(
    request: VerifyArtistRequest
  ): Promise<{ success: boolean; message: string }> {
    const endpoint = `/artist/verify`;
    const body = JSON.stringify(request);
    return fetchFromAPIWithoutAuth<{ success: boolean; message: string }>(
      endpoint,
      (data) => data as { success: boolean; message: string },
      'POST',
      body
    );
  }
  
  // ------------------------------------
  // Artist Search
  // ------------------------------------
  export async function requestMagicLink(userEmail: string) {
    const endpoint = `/auth/requestMagicLink`;
    const body = JSON.stringify({ userEmail });
    return fetchFromAPIWithoutAuth(endpoint, (data) => data, 'POST', body);
  }
  
  export async function searchArtists(
    partialName: string,
    limit: number = 10,
    lastEvaluatedKey?: string
  ): Promise<ArtistSearchResponse> {
    const endpoint = `/artist/operations/public`;
    const body: {
      action: string;
      partialName: string;
      limit: string;
      lastEvaluatedKey?: string;
    } = { 
      action: 'search', 
      partialName: encodeURIComponent(partialName),
      limit: limit.toString()
    };
  
    if (lastEvaluatedKey) {
      body.lastEvaluatedKey = lastEvaluatedKey;
    }
  
    return fetchFromAPIWithoutAuth<ArtistSearchResponse>(
      endpoint,
      (data) => data as ArtistSearchResponse,
      'POST',
      JSON.stringify(body)
    );
  }

export async function globalSearch(
  query: string,
  types?: ('people' | 'artists')[],
  limit?: number
): Promise<GlobalSearchResponse> {
  const endpoint = `/search/global`;
  const body = {
    query: query.trim(),
    types: types || ['people', 'artists'],
    limit: limit || 10
  };

  return fetchFromAPI<GlobalSearchResponse>(
    endpoint,
    (data) => data as GlobalSearchResponse,
    'POST',
    JSON.stringify(body)
  );
}
  // ------------------------------------
  // Coral-Related Functions
  // ------------------------------------
  export async function getCoralFromAPI(id: string): Promise<ClientCoral> {
    const body = JSON.stringify({ action: 'getCoral', id });
    return fetchFromAPI<ClientCoral>(
      CORAL_OPERATIONS,
      (data) => data as ClientCoral,
      'POST',
      body
    );
  }
  
  export async function getCuratedCoralFromAPI(id: string): Promise<ClientCoral> {
    const body = JSON.stringify({ action: 'getCuratedCoral', id });
    return fetchFromAPIWithoutAuth<ClientCoral>(
      CORAL_PUBLIC_OPERATIONS,
      (data) => data as ClientCoral,
      'POST',
      body
    );
  }
  
  export async function getSharedArtistPoolCoralDetails(
    artistPoolGuid: string
  ): Promise<{ coralName: string; coralGuid: string }> {
    const body = JSON.stringify({ action: 'getSharedArtistPoolCoralDetails', artistPoolGuid });
    return fetchFromAPI<{ coralName: string; coralGuid: string }>(
      CORAL_OPERATIONS,
      (data) => data as { coralName: string; coralGuid: string },
      'POST',
      body,
      false
    );
  }
  
  export async function getwearecoralImpact(
    featured: boolean = false,
    limit: number = 12
  ): Promise<ArtistAnalytics[]> {
    const params = new URLSearchParams();
    if (featured) params.append('featured', 'true');
    params.append('limit', limit.toString());
    
    const endpoint = `/analytics/impact?${params.toString()}`;
    return fetchFromAPI(endpoint, (data) => data as ArtistAnalytics[], 'GET', undefined, false);
  }
  
  export async function addTagToCoral(coralId: string, tagText: string) {
    const endpoint = `/coral/tag/operations`;
    const body = JSON.stringify({ action: 'addTag', coralId, tagText });
    return fetchFromAPI(endpoint, (data) => data, 'POST', body);
  }

  export async function removeTagFromCoral(coralId: string, tag: Tag) {
    const endpoint = `/coral/tag/operations`;
    const body = JSON.stringify({ action: 'removeTag', coralId, tag });
    return fetchFromAPI(endpoint, (data) => data, 'POST', body);
  }

  export async function retrieveTagsForCoral(coralId: string): Promise<Tag[]> {
    const endpoint = `/coral/tag/operations`;
    const body = JSON.stringify({ action: 'retrieveTags', coralId });
    const response = (await fetchFromAPI(endpoint, (data) => data, 'POST', body)) as { tags: Tag[] };
    return Array.isArray(response.tags) ? response.tags : [];
  }
  
  export async function getCategoriesAndGenresForCoralBrowser(): Promise<
    { categorization: Tag; genres: Tag[] }[]
  > {
    const endpoint = `/content/handler`;
    const response = (await fetchFromAPI(
      endpoint,
      (data) => data,
      'POST',
      JSON.stringify({ action: 'getCategoriesAndGenres' })
    )) as {
      tagsForCoralBrowser: {
        categorization: { tagType: string; tagText: string };
        genres: { tagType: string; tagText: string }[];
      }[];
    };
    return response.tagsForCoralBrowser.map(({ categorization, genres }) => ({
      categorization: { ...categorization, name: categorization.tagText, type: categorization.tagType },
      genres: genres.map((genre) => ({ ...genre, name: genre.tagText, type: genre.tagType })),
    }));
  }
  
  export async function getAllCoralTags(limit: number = Infinity): Promise<string[]> {
    const endpoint = `/content/handler`;
    const response = (await fetchFromAPI(
      endpoint,
      (data) => data,
      'POST',
      JSON.stringify({ action: 'getAllTags', ...(Number.isFinite(limit) && { limit }) })
    )) as { tags: string[] };
    return Array.isArray(response.tags) ? response.tags : [];
  }
  
  export async function getCoralsForTag(
    tag: Tag
  ): Promise<{ coralName: string; coralGuid: string; artists: string[] }[]> {
    const endpoint = `/content/handler`;
    const response = (await fetchFromAPI(
      endpoint,
      (data) => data,
      'POST',
      JSON.stringify({ action: 'getCoralsForTag', tagType: tag.tagType, tagText: tag.tagText })
    )) as {
      corals: { coralName: string; coralGuid: string; artists: string[] }[];
    };
    return Array.isArray(response.corals) ? response.corals : [];
  }
  
  export async function getRecentSharedCorals(
    limit: number = 8
  ): Promise<{ coralName: string; coralGuid: string; artists: string[] }[]> {
    const endpoint = `/content/handler`;
    const response = (await fetchFromAPI(
      endpoint,
      (data) => data,
      'POST',
      JSON.stringify({ action: 'getRecentSharedCorals', limit }),
      false
    )) as {
      corals: { coralName: string; coralGuid: string; artists: string[] }[];
    };
    return Array.isArray(response.corals) ? response.corals : [];
  }
  
  export async function createCoralFromAPI(
    coralType: CoralType, 
    artistId?: string
  ): Promise<BaseCoral> {
    const body = JSON.stringify({
      action: 'createCoral',
      coralType,
      ...(artistId && { artistId }),
    });
    return fetchFromAPI<BaseCoral>(CORAL_OPERATIONS, (data) => data as BaseCoral, 'POST', body, true);
  }
  
  export async function createCoralPublic(
    artistGuid?: string, 
    cache?: boolean
  ): Promise<BaseCoral> {
    // Only the blank-coral seed is cached in S3. The per-artist seed cache was never wired correctly
    // (its write key lacked the `:<CoralType.USER>` segment this read expects), so it always missed —
    // artist requests skip S3 and go straight to the API.
    if (!artistGuid) {
      const s3Data = await fetchFromS3<BaseCoral>(`coral-with-artists-cache/blank:${CoralType.USER}.json`);
      if (s3Data) {
        return s3Data;
      }
    }

    // Fall back to the API (always, for the artist path)
    const endpoint = `/coral/create/public`;
    const body = JSON.stringify({
      ...(artistGuid && { artistId: artistGuid }),
      cache: cache || false,
      coralType: CoralType.USER,
    });
    return fetchFromAPIWithoutAuth<BaseCoral>(endpoint, (data) => data as BaseCoral, 'POST', body, true);
  }
  
  export async function updateCoralSharingSettings(
    coralGuid: string, 
    isShared: boolean
  ): Promise<UpdateCoralVisibilityResponse> {
    const body = JSON.stringify({ action: 'updateSharingSettings', coralGuid, isShared });
    return fetchFromAPI<UpdateCoralVisibilityResponse>(
      CORAL_OPERATIONS, 
      (data) => data as UpdateCoralVisibilityResponse, 
      'POST', 
      body
    );
  }
  
  export async function updateCoralCuratedSettings(
    coralGuid: string, 
    isCurated: boolean
  ): Promise<UpdateCoralCuratedResponse> {
    const body = JSON.stringify({ action: 'updateCuratedSettings', coralGuid, isCurated });
    return fetchFromAPI<UpdateCoralCuratedResponse>(
      CORAL_OPERATIONS, 
      (data) => data as UpdateCoralCuratedResponse, 
      'POST', 
      body
    );
  }
  
  export async function saveCoralToAPI(coral: ClientCoral): Promise<{ guid: string }> {
    const endpoint = `/user/operations`;
    const body = JSON.stringify({ action: 'saveCoral', ...coral });
    return fetchFromAPI<{ guid: string }>(
      endpoint,
      (data) => data as { guid: string },
      'POST',
      body,
      true
    );
  }
  
  export async function saveTemporaryCoralToAPI(
    coral: ClientCoral
  ): Promise<{ tempCoralId: string }> {
    const endpoint = `/coral/savetemporary`;
    const body = JSON.stringify(coral);
    return fetchFromAPIWithoutAuth<{ tempCoralId: string }>(
      endpoint, 
      (data) => data as { tempCoralId: string }, 
      'POST', 
      body, 
      true
    );
  }
  
  export async function simulateCoral(coral: ClientCoral): Promise<SimulateCoralResponse> {
    const body = JSON.stringify({ action: 'simulateCoral', coral });
    return fetchFromAPI<SimulateCoralResponse>(
      CORAL_OPERATIONS, 
      (data) => data as SimulateCoralResponse, 
      'POST', 
      body
    );
  }
  
  export async function simulateCoralWithoutAuth(
    coral: ClientCoral
  ): Promise<SimulateCoralResponse> {
    const body = JSON.stringify({ action: 'simulateSharedCoral', coral });
    return fetchFromAPIWithoutAuth<SimulateCoralResponse>(
      CORAL_PUBLIC_OPERATIONS, 
      (data) => data as SimulateCoralResponse, 
      'POST', 
      body
    );
  }

  // ------------------------------------
  // Cross-User Coral Viewing Functions
  // ------------------------------------

  /**
   * Gets a user's coral for cross-user viewing with filtered data.
   * Allows any authenticated user to view USER corals with sensitive information removed.
   * 
   * @param userId - The ID of the user who owns the coral
   * @param coralId - The ID of the coral to retrieve
   * @returns Promise<FilteredClientCoral> - Filtered coral data
   */
  export async function getUserCoral(userId: string, coralId: string): Promise<FilteredClientCoral> {
    const body = JSON.stringify({ action: 'getCoralForViewer', userId, coralId });
    return fetchFromAPI<FilteredClientCoral>(
      CORAL_OPERATIONS,
      (data) => data as FilteredClientCoral,
      'POST',
      body
    );
  }

  /**
   * Gets a list of a user's active USER corals for profile display.
   * Returns basic coral information without sensitive data.
   * 
   * @param userId - The ID of the user whose corals to retrieve
   * @returns Promise<Array> - Array of basic coral information
   */
  export async function getUserCorals(userId: string): Promise<{ guid: string; coralName: string; type: CoralType; createdAt: string }[]> {
    const body = JSON.stringify({ action: 'getUserCorals', userId });
    const response = await fetchFromAPI<{ corals: { guid: string; coralName: string; type: CoralType; createdAt: string }[] }>(
      CORAL_OPERATIONS,
      (data) => data,
      'POST',
      body
    );
    return response.corals;
  }
  
  export async function editSignal(
    coralId: string,
    timestamp: string,
    artistId: string,
    message: string
  ): Promise<void> {
    const endpoint = `/signal/operations`;
    const body = JSON.stringify({ action: 'editSignal', coralId, timestamp, artistId, message });
    await fetchFromAPI<void>(endpoint, (data) => data, 'POST', body);
  }

  export async function deleteSignal(
    coralId: string,
    timestamp: string,
    artistId: string
  ): Promise<void> {
    const endpoint = `/signal/operations`;
    const body = JSON.stringify({ action: 'deleteSignal', coralId, timestamp, artistId });
    await fetchFromAPI<void>(endpoint, (data) => data, 'POST', body);
  }

  export async function addSignal(
    coralId: string,
    signal: {
        signalType: 'artist' | 'coral';
        artistId?: string;
        artistName?: string;
        message: string;
    }
): Promise<void> {
    const endpoint = `/signal/operations`;
    const body = JSON.stringify({ action: 'addSignal', coralId, signal });
    await fetchFromAPI<void>(endpoint, (data) => data, 'POST', body);
}

  /** A coral's payment history, its summary (including what a refund would repay: the unpaid share and the fees on it), and the refund request to show the member. */
  export interface CoralPaymentsResponse {
    payments: ProcessedCoralPayments[];
    summary: CoralPaymentsSummary;
    refundRequest: MemberRefundRequestStatus | null;
  }

  export async function getCoralPayments(
    coralId: string, 
    startDate?: string, 
    endDate?: string
  ): Promise<CoralPaymentsResponse> {
    const queryParams = new URLSearchParams({
      coralId: coralId,
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    });
  
    const endpoint = `/coral/payments?${queryParams.toString()}`;
    return fetchFromAPI<CoralPaymentsResponse>(
      endpoint,
      (data) => data as CoralPaymentsResponse,
      'GET',
      undefined,
      true
    );
  }

/**
 * The member refund request body: exactly the fields the endpoint accepts. The amount is always the
 * server's, fixed when an admin approves.
 */
export type CoralRefundRequestBody = {
  coralId: string;
  cancelCoral: boolean;
  notes?: string;
} & (
  | { paymentMethod: RefundPaymentMethod.PAYPAL; paymentDetails: PayPalDetails }
  | { paymentMethod: RefundPaymentMethod.BANK_TRANSFER; paymentDetails: BankTransferDetails }
);

/** Request a refund of the part of a coral's contributions not yet paid to artists. */
export async function requestCoralRefund(refundData: CoralRefundRequestBody): Promise<{ 
  message: string; 
  refundId: string; 
  refundRequest: Omit<RefundRequest, 'paymentDetails'>; 
}> {
  const endpoint = `/coral/requestRefund`;
  const body = JSON.stringify(refundData);
  return fetchFromAPI<{ message: string; refundId: string; refundRequest: Omit<RefundRequest, 'paymentDetails'>; }>(
    endpoint,
    (data) => data as { message: string; refundId: string; refundRequest: Omit<RefundRequest, 'paymentDetails'>; },
    'POST',
    body,
    true
  );
}
  
  // ------------------------------------
  // User-Related Functions
  // ------------------------------------
  export async function updateUserArtistConnections(
    artistConnections: Array<{ artistName: string; artistGuid: string }>
  ): Promise<void> {
    const endpoint = `/user/operations`;
    const body = JSON.stringify({ action: 'updateArtistConnections', artistConnections });
    await fetchFromAPI<{ message: string }>(
      endpoint,
      (data) => data as { message: string },
      'POST',
      body
    );
  }

  export async function getUserArtistConnections(): Promise<
    Array<{ artistName: string; artistGuid: string }>
  > {
    const endpoint = `/user/operations`;
    return fetchFromAPI<Array<{ artistName: string; artistGuid: string }>>(
      endpoint,
      (data) => data as Array<{ artistName: string; artistGuid: string }>,
      'POST',
      JSON.stringify({ action: 'getArtistConnections' })
    );
  }
  
  export async function createArtistApplication(
    artistApplication: ArtistApplication
  ): Promise<{ applicationId: string; status: string; artistName: string }> {
    const endpoint = `/user/application`;
    const body = JSON.stringify({ application: artistApplication });
    return fetchFromAPI<{ applicationId: string; status: string; artistName: string }>(
      endpoint,
      (data) => data as { applicationId: string; status: string; artistName: string },
      'POST',
      body
    );
  }
  
  export async function getUserArtistApplications(): Promise<
    { applicationId: string; status: string; artistName: string }[]
  > {
    const endpoint = `/user/applications`;
    return fetchFromAPI<{ applicationId: string; status: string; artistName: string }[]>(
      endpoint, 
      (data) => data as { applicationId: string; status: string; artistName: string }[], 
      'GET'
    );
  }
  
  export async function createUserIdentityVerification(
    userIdentityVerification: UserIdentityVerification
  ): Promise<{ verificationId: string; status: string }> {
    const endpoint = `/user/verification/operations`;
    const body = JSON.stringify({ action: 'create', verification: userIdentityVerification });
    return fetchFromAPI<{ verificationId: string; status: string }>(
      endpoint,
      (data) => data as { verificationId: string; status: string },
      'POST',
      body
    );
  }

  // Connect a curated coral
  export async function connectCuratedCoral(curatedCoralId: string): Promise<void> {
    const endpoint = `/user/operations`;
    const body = JSON.stringify({ action: 'connectCoral', curatedCoralId });
    await fetchFromAPI<void>(endpoint, (data) => data, 'POST', body);
  }

  // Get connected curated corals
  export async function getConnectedCuratedCorals(): Promise<UserCoralConnection[]> {
    const endpoint = `/user/operations`;
    return fetchFromAPI<UserCoralConnection[]>(
      endpoint,
      (data) => data as UserCoralConnection[],
      'POST',
      JSON.stringify({ action: 'getConnectedCorals' }),
      false
    );
  }

  // Disconnect a curated coral
  export async function disconnectCuratedCoral(curatedCoralId: string): Promise<void> {
    const endpoint = `/user/operations`;
    const body = JSON.stringify({ action: 'disconnectCoral', curatedCoralId });
    await fetchFromAPI<void>(endpoint, (data) => data, 'POST', body);
  }
  
  export async function updateUser(user: Partial<User>): Promise<User> {
    const endpoint = `/user/operations`;
    const body = JSON.stringify({ action: 'updateUser', ...user });
    return fetchFromAPI<User>(
      endpoint,
      (data) => data as User,
      'POST',
      body,
      false
    );
  }

  export async function getUserFromAPI(userId: string): Promise<User> {
    return fetchFromAPI<User>(
      `/user/operations`,
      (data) => data as User,
      'POST',
      JSON.stringify({ action: 'getUser', id: userId }),
      false
    );
  }
  
  export async function getUserDashboard(): Promise<{ 
    firstName: string; 
    connectedArtists: ArtistAnalytics[]; 
    hasActiveCorals: boolean;
    activeCoralCount: number;
  }> {
    return fetchFromAPI<{ 
      firstName: string; 
      connectedArtists: ArtistAnalytics[]; 
      hasActiveCorals: boolean;
      activeCoralCount: number;
    }>(
      '/user/operations', 
      (data) => data as { 
        firstName: string; 
        connectedArtists: ArtistAnalytics[]; 
        hasActiveCorals: boolean;
        activeCoralCount: number;
      }, 
      'POST', 
      JSON.stringify({ action: 'getDashboard' }), 
      true
    );
  }

  export async function getUserCoralData(): Promise<{ activeCorals: ClientCoral[]; paymentSummary: PaymentSummary }> {
    return fetchFromAPI<{ activeCorals: ClientCoral[]; paymentSummary: PaymentSummary }>(
      '/user/operations', 
      (data) => data as { activeCorals: ClientCoral[]; paymentSummary: PaymentSummary }, 
      'POST', 
      JSON.stringify({ action: 'getUserCorals' }), 
      true
    );
  }

  /**
   * Get user's personalized content feed.
   * Uses 5-phase cached loading: user graph → feed indices → merge/dedupe → content items → display names
   */
  export async function getUserContentFeed(
    cursor?: string | null,
    limit?: number
  ): Promise<{ contentFeed: ContentItem[]; lastEvaluatedKey: string | null }> {
    const body: { action: string; cursor?: string; limit?: number } = { action: 'getContentFeed' };
    if (cursor) {
      body.cursor = cursor;
    }
    if (limit) {
      body.limit = limit;
    }

    return fetchFromAPI<{ contentFeed: ContentItem[]; lastEvaluatedKey: string | null }>(
      '/user/operations',
      (data) => data as { contentFeed: ContentItem[]; lastEvaluatedKey: string | null },
      'POST',
      JSON.stringify(body),
      true
    );
  }

  export async function getContentItem(contentId: string): Promise<ContentItem> {
    return fetchFromAPI<ContentItem>(
      '/content/handler',
      (data) => data as ContentItem,
      'POST',
      JSON.stringify({ action: 'getContentItem', contentId }),
      true
    );
  }

  export async function getUserActivity(
    userId: string,
    lastEvaluatedKey?: string,
    limit?: number
  ): Promise<ContentFeed> {
    const endpoint = `/user/operations`;
    const body = JSON.stringify({
      action: 'getUserActivity',
      userId,
      ...(lastEvaluatedKey && { lastEvaluatedKey }),
      ...(limit && { limit }),
    });

    return fetchFromAPI<ContentFeed>(
      endpoint,
      (data) => data as ContentFeed,
      'POST',
      body,
      true
    );
  }
  
  // ------------------------------------
  // Subscription Functions
  // ------------------------------------
  export async function createCoralSubscription(
    guid: string, 
    subscriptionId: string
  ): Promise<{ guid: string }> {
    const endpoint = `/subscription/create`;
    const body = JSON.stringify({ guid, subscriptionId });
    return fetchFromAPI<{ guid: string }>(
      endpoint, 
      (data) => data as { guid: string }, 
      'POST', 
      body, 
      true
    );
  }
  
  export async function pauseCoralSubscription(guid: string): Promise<{ guid: string }> {
    const endpoint = `/subscription/pause`;
    const body = JSON.stringify({ guid });
    return fetchFromAPI<{ guid: string }>(
      endpoint, 
      (data) => data as { guid: string }, 
      'POST', 
      body, 
      true
    );
  }
  
  export async function reactivateCoralSubscription(guid: string): Promise<{ guid: string }> {
    const endpoint = `/subscription/reactivate`;
    const body = JSON.stringify({ guid });
    return fetchFromAPI<{ guid: string }>(
      endpoint, 
      (data) => data as { guid: string }, 
      'POST', 
      body, 
      true
    );
  }
  
  export async function cancelCoralSubscription(guid: string): Promise<void> {
    const endpoint = `/subscription/cancel`;
    const body = JSON.stringify({ guid });
    await fetchFromAPI<void>(
      endpoint, 
      (data) => data as void, 
      'POST', 
      body, 
      true
    );
  }
  
  export async function getPayPalPlanIdForPledgeAmount(
    amount: number
  ): Promise<{ planId: string }> {
    const endpoint = `/subscription/getPayPalPlanId?amount=${amount}`;
    return fetchFromAPIWithoutAuth<{ planId: string }>(
      endpoint, 
      (data) => data as { planId: string }, 
      'GET'
    );
  }
  
  // ------------------------------------
  // Named Export (for reuse if needed)
  // ------------------------------------
  export { fetchFromAPI };

  export async function getEcosystemContent(): Promise<{
    newCorals: CoralContentSummary[];
    recentlyProcessedCorals: CoralContentSummary[];
    recentSignals: SignalRecord[];
    musicContent: ContentItem[];
    sharedTracks: ContentItem[];
    recentComments: ContentItem[];
}> {
    const body = JSON.stringify({ action: 'getEcosystem' });
    
    return fetchFromAPI(
        '/content/handler',
        (data) => data as { 
            newCorals: CoralContentSummary[]; 
            recentlyProcessedCorals: CoralContentSummary[]; 
            recentSignals: SignalRecord[];
            musicContent: ContentItem[];
            sharedTracks: ContentItem[];
            recentComments: ContentItem[];
        },
        'POST',
        body,
        false
    );
}

// ------------------------------------
// Comment-Related Functions
// ------------------------------------

// Define response interfaces at the top for better organization
export interface GetCommentsResponse {
  comments: CommentRecord[];
  lastEvaluatedKey?: string;
  totalCount?: number;
}

export interface CreateCommentResponse {
  comment: CommentRecord;
}

export interface UpdateCommentResponse {
  comment: CommentRecord;
}

export interface DeleteCommentResponse {
  success: boolean;
  message?: string;
  deleteType?: 'soft' | 'hard';
}

export interface ReactionResponse {
  success?: boolean;
  currentUserReaction?: ReactionRecord | null;
  totalReactions?: number;
  comment?: CommentRecord;
}

/**
 * Get comments for a content item with optional filtering by entity
 * @param request Parameters for comment retrieval
 * @returns A feed of comments
 */
export async function getComments(request: GetCommentsRequest): Promise<CommentFeed> {
  const endpoint = `/content/comment-handler`;
  const body = JSON.stringify({
    action: 'getComments',
    ...request,
    // Backend expects includeReplies as string 'true', not boolean
    ...(request.includeReplies && { includeReplies: 'true' }),
    // Use lastKey for consistency with backend parameter
    ...(request.lastEvaluatedKey && { lastKey: request.lastEvaluatedKey, lastEvaluatedKey: undefined })
  });
  
  const response = await fetchFromAPI<GetCommentsResponse>(
    endpoint,
    (data: GetCommentsResponse) => data,
    'POST',
    body,
    false
  );
  
  return {
    comments: response.comments || [],
    lastEvaluatedKey: response.lastEvaluatedKey,
    totalCount: response.totalCount
  };
}

/**
 * Create a new comment on a content item
 * @param request Parameters for comment creation
 * @returns The created comment record
 */
export async function createComment(request: CreateCommentRequest): Promise<CommentRecord> {
  const endpoint = `/content/comment-handler`;
  const body = JSON.stringify({ action: 'createComment', ...request });
  
  const response = await fetchFromAPI<CreateCommentResponse>(
    endpoint,
    (data: CreateCommentResponse) => data,
    'POST',
    body,
    false
  );
  
  return response.comment;
}

/**
 * Update an existing comment
 * @param request Parameters for comment update
 * @returns The updated comment record
 */
export async function updateComment(request: UpdateCommentRequest): Promise<CommentRecord> {
  const endpoint = `/content/comment-handler`;
  const body = JSON.stringify({ action: 'updateComment', ...request });
  
  const response = await fetchFromAPI<UpdateCommentResponse>(
    endpoint,
    (data: UpdateCommentResponse) => data,
    'POST',
    body,
    false
  );
  
  return response.comment;
}

/**
 * Delete a comment
 * @param request Parameters for comment deletion
 * @returns Success message
 */
export async function deleteComment(request: DeleteCommentRequest): Promise<{ success: boolean }> {
  const endpoint = `/content/comment-handler`;
  const body = JSON.stringify({ action: 'deleteComment', ...request });
  
  const response = await fetchFromAPI<DeleteCommentResponse>(
    endpoint,
    (data: DeleteCommentResponse) => data,
    'POST',
    body,
    false
  );
  
  return { success: response.success || false };
}

/**
 * React to a comment (resonate/un-resonate)
 * @param request Parameters for the reaction
 * @returns The updated reaction status
 */
export async function reactToComment(request: ReactionRequest): Promise<{ 
  success: boolean;
  currentUserReaction: ReactionRecord | null;
  totalReactions: number;
}> {
  const endpoint = `/content/comment-handler`;
  const body = JSON.stringify({ 
    action: 'react',
    contentId: request.contentId, 
    commentId: request.commentId, 
    reactionAction: request.action 
  });
  
  const response = await fetchFromAPI<ReactionResponse>(
    endpoint,
    (data: ReactionResponse) => data,
    'POST',
    body,
    false
  );
  
  // Process the response based on format
  if (response.comment) {
    // New format with comment object
    return {
      success: true,
      currentUserReaction: response.currentUserReaction || null,
      totalReactions: response.comment.reactions?.resonates || 0
    };
  }
  
  // Original format
  return {
    success: response.success || true,
    currentUserReaction: response.currentUserReaction || null,
    totalReactions: response.totalReactions || 0
  };
}

/**
 * Create a new content item
 * @param request Parameters for content item creation
 * @returns The created content item
 */
export async function createContentItem(request: CreateContentItemRequest): Promise<ContentItemResponse["contentItem"]> {
  const endpoint = `/content/handler`;
  const body = JSON.stringify({ action: 'create', ...request });

  const response = await fetchFromAPI<ContentItemResponse>(
    endpoint,
    (data: ContentItemResponse) => data,
    'POST',
    body,
    false
  );

  return response.contentItem;
}

/**
 * Return a gesture (smile back, nod back, etc.)
 * Only the recipient of a gesture can return it.
 * @param contentId The ID of the gesture content item to return
 * @returns The updated gesture with returnedGesture metadata
 */
export async function returnGesture(contentId: string): Promise<ContentItem> {
  const endpoint = `/content/handler`;
  const body = JSON.stringify({ action: 'returnGesture', contentId });

  const response = await fetchFromAPI<{ gesture: ContentItem }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );

  return response.gesture;
}

/**
 * Get a list of entity content items that can have comments
 * @param entityType The type of entity (artist, musicRelease, musicTrack)
 * @param entityId The ID of the entity
 * @returns List of content items
 */
export async function getEntityContent(
  entityType: EntityType,
  entityId: string,
  lastEvaluatedKey?: string,
  limit?: number,
  contentType?: string
): Promise<{
  contentFeed: ContentFeed;
}> {
  const endpoint = `/content/handler`;
  const body = JSON.stringify({
    action: 'get',
    entityType,
    entityId,
    lastKey: lastEvaluatedKey,
    limit,
    contentType
  });
  
  // The API returns a response with contentFeed structure
  return fetchFromAPI<{
    contentFeed: ContentFeed;
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Get content created by a specific user, optionally filtered by entity type
 * @param userId The ID of the user whose content to fetch
 * @param entityType Optional filter for entity type (e.g., 'musicTrack')
 * @param limit Maximum number of items to return
 * @returns List of content items created by the user
 */
export async function getUserCreatedContent(
  userId: string,
  entityType?: EntityType,
  limit?: number
): Promise<{
  contentFeed: ContentFeed;
}> {
  const endpoint = `/content/handler`;
  const body = JSON.stringify({
    action: 'getUserContent',
    userId,
    entityType,
    limit: limit || 50
  });

  return fetchFromAPI<{
    contentFeed: ContentFeed;
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Get content shared TO a specific user's dancefloor
 * This retrieves content where the user is the RECIPIENT, not the creator.
 * @param targetUserId - The ID of the user whose dancefloor to fetch content for
 * @param contentType - Optional filter for content type
 * @param limit - Maximum number of items to return
 * @returns List of content items shared to this user's dancefloor
 */
export async function getTargetUserContent(
  targetUserId: string,
  contentType?: string,
  limit?: number
): Promise<{
  contentFeed: ContentFeed;
}> {
  const endpoint = `/content/handler`;
  const body = JSON.stringify({
    action: 'getTargetUserContent',
    targetUserId,
    contentType,
    limit: limit || 50
  });

  return fetchFromAPI<{
    contentFeed: ContentFeed;
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Get dancefloor feed for a user or artist (cached).
 * Returns gestures and music shares for the entity's dancefloor.
 * @param entityType - 'user' or 'artist'
 * @param entityId - The user ID or artist ID
 * @param limit - Maximum number of items to return (default 100)
 * @returns Dancefloor content items
 */
export async function getDancefloorFeed(
  entityType: 'user' | 'artist' | 'community',
  entityId: string,
  limit?: number
): Promise<{
  items: ContentItem[];
  fromCache: boolean;
}> {
  const endpoint = `/content/handler`;
  const body = JSON.stringify({
    action: 'getDancefloorFeed',
    entityType,
    entityId,
    limit: limit || 100
  });

  return fetchFromAPI<{
    items: ContentItem[];
    fromCache: boolean;
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Get a random listening room album recommendation
 * @param excludeId - Optional content ID to exclude from selection
 * @returns A listening room recommendation
 */
export async function getListeningRoomAlbum(excludeId?: string): Promise<{
  recommendation?: ContentItem;
  isEmpty?: boolean;
  message?: string;
}> {
  const endpoint = `/content/handler`;
  const body = JSON.stringify({ 
    action: 'getListeningRoomAlbum',
    ...(excludeId && { excludeId })
  });
  
  const response = await fetchFromAPI<{
    recommendation?: ContentItem;
    isEmpty?: boolean;
    message?: string;
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
  
  return response;
}

/**
 * Get a specific listening room recommendation by contentId
 * @param contentId - The content ID of the specific recommendation
 * @returns The specific listening room recommendation
 */
export async function getListeningRoomRecommendation(contentId: string): Promise<{
  recommendation?: ContentItem;
  exists?: boolean;
  message?: string;
}> {
  const endpoint = `/content/handler`;
  const body = JSON.stringify({ 
    action: 'get',
    entityType: 'contentItem',
    entityId: contentId
  });
  
  const response = await fetchFromAPI<{
    contentFeed?: {
      items: ContentItem[];
    };
    message?: string;
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
  
  // Check if we got a valid listening room recommendation
  if (response.contentFeed?.items?.length === 1) {
    const item = response.contentFeed.items[0];
    if (item.contentType === 'listeningRoom') {
      return {
        recommendation: item,
        exists: true
      };
    }
  }
  
  return {
    exists: false,
    message: 'Listening room recommendation not found'
  };
}

// ------------------------------------
// Music Resolution Functions
// ------------------------------------

/**
 * Resolve a music URL to track or release information
 * @param request Music URL to resolve
 * @returns Resolved music data (track or release)
 */
export async function resolveMusicLink(request: MusicLinkResolveRequest): Promise<MusicLinkResolveResponse> {
  const endpoint = `/music/resolve-link`;
  const body = JSON.stringify(request);
  
  const response = await fetchFromAPI<MusicLinkResolveResponse>(
    endpoint,
    (data: MusicLinkResolveResponse) => data,
    'POST',
    body,
    false
  );
  
  return response;
}

/**
 * Submit music content for unknown artists (creates pending entities)
 * @param musicUrl Music URL to submit
 * @param contentType Type of content (discussion, review, listeningRoom)
 * @param commentText User's comment text
 * @returns Success response with pending entity ID
 */
export async function submitPendingMusicContent(
  musicUrl: string,
  contentType: 'discussion' | 'review' | 'listeningRoom',
  commentText: string
): Promise<{ success: boolean; message: string; pendingEntityId: string }> {
  const endpoint = `/music/submit-pending-content`;
  const body = JSON.stringify({ musicUrl, contentType, commentText });
  
  const response = await fetchFromAPI<{ success: boolean; message: string; pendingEntityId: string }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
  
  return response;
}

// ------------------------------------
// User Alignment Functions
// ------------------------------------

/**
 * Create an alignment between users (align with another user)
 * @param targetUserId The ID of the user to align with
 * @returns Success confirmation
 */
export async function createAlignment(targetUserId: string): Promise<{ success: boolean; message: string }> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'create', targetUserId });
  
  return fetchFromAPI<{ success: boolean; message: string }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Remove an alignment between users (stop aligning with another user)
 * @param targetUserId The ID of the user to stop aligning with
 * @returns Success confirmation
 */
export async function removeAlignment(targetUserId: string): Promise<{ success: boolean; message: string }> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'remove', targetUserId });
  
  return fetchFromAPI<{ success: boolean; message: string }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Get all users that the current user is aligned with
 * @returns List of user alignments
 */
export async function getUserAlignments(): Promise<{ alignments: Array<{ targetUserId: string; createdAt: string }> }> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'list' });

  return fetchFromAPI<{ alignments: Array<{ targetUserId: string; createdAt: string }> }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Search aligned users by display name (for @ mention autocomplete)
 * @param query Search query string
 * @param limit Maximum number of results to return (default: 10)
 * @returns List of matching aligned users
 */
export async function searchAlignedUsers(query: string, limit: number = 10): Promise<{
  users: Array<{
    targetUserId: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
  }>
}> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'search', query, limit });

  return fetchFromAPI<{
    users: Array<{
      targetUserId: string;
      displayName?: string;
      firstName?: string;
      lastName?: string;
    }>
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Get all users that a specific user is aligned with (for viewing other user profiles)
 * @param userId The ID of the user whose alignments to retrieve
 * @returns List of user alignments with user details for the specified user
 */
export async function getUserAlignmentsByUserId(userId: string): Promise<{
  alignments: Array<{
    userId: string;
    displayName?: string;
    firstName?: string;
    createdAt: string;
  }>
}> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'listByUserId', userId });

  return fetchFromAPI<{
    alignments: Array<{
      userId: string;
      displayName?: string;
      firstName?: string;
      createdAt: string;
    }>
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Check if current user is aligned with a specific user
 * @param targetUserId The ID of the user to check alignment status with
 * @returns Alignment status
 */
export async function checkAlignmentStatus(targetUserId: string): Promise<{ isAligned: boolean; alignmentDate?: string }> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'status', targetUserId });
  
  return fetchFromAPI<{ isAligned: boolean; alignmentDate?: string }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

// ------------------------------------
// Artist Alignment Functions
// ------------------------------------

/**
 * Create an artist alignment (follow an artist)
 * @param targetArtistId - ID of the artist to follow
 * @returns Success confirmation
 */
export async function createArtistAlignment(targetArtistId: string): Promise<{ success: boolean; message: string }> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'createArtist', targetArtistId });
  
  return fetchFromAPI<{ success: boolean; message: string }>(
    endpoint,
    (data) => ({ success: true, message: data.message }),
    'POST',
    body,
    false
  );
}

/**
 * Remove an artist alignment (unfollow an artist)
 * @param targetArtistId - ID of the artist to unfollow
 * @returns Success confirmation
 */
export async function removeArtistAlignment(targetArtistId: string): Promise<{ success: boolean; message: string }> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'removeArtist', targetArtistId });
  
  return fetchFromAPI<{ success: boolean; message: string }>(
    endpoint,
    (data) => ({ success: true, message: data.message }),
    'POST',
    body,
    false
  );
}

/**
 * Check artist alignment status (is user following this artist?)
 * @param targetArtistId - ID of the artist to check
 * @returns Alignment status
 */
export async function checkArtistAlignmentStatus(targetArtistId: string): Promise<{ isAligned: boolean; alignmentDate?: string }> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'statusArtist', targetArtistId });

  return fetchFromAPI<{ isAligned: boolean; alignmentDate?: string }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

/**
 * Get financial contributors for an artist (users who back them via corals)
 * @param artistId - ID of the artist
 * @returns List of contributors with basic user info
 */
export async function getArtistContributors(artistId: string): Promise<{
  contributors: Array<{
    userId: string;
    displayName?: string;
    firstName?: string;
    createdAt: string;
  }>
}> {
  const endpoint = `/user/alignment`;
  const body = JSON.stringify({ action: 'listArtistContributors', artistId });

  return fetchFromAPI<{
    contributors: Array<{
      userId: string;
      displayName?: string;
      firstName?: string;
      createdAt: string;
    }>
  }>(
    endpoint,
    (data) => data,
    'POST',
    body,
    false
  );
}

// ------------------------------------
// Notification Functions
// ------------------------------------

/**
 * Get user notifications with pagination support
 * @param request Parameters for retrieving notifications
 * @returns Notifications with pagination and unread count
 */
export async function getUserNotifications(request: GetNotificationsRequest = {}): Promise<GetNotificationsResponse> {
  const endpoint = `/user/notifications`;
  const body = JSON.stringify({ action: 'get', ...request });
  
  return fetchFromAPI<GetNotificationsResponse>(
    endpoint,
    (data) => data as GetNotificationsResponse,
    'POST',
    body,
    false
  );
}

/**
 * Mark notifications as viewed by updating user's view timestamp
 * @param request Optional timestamp to mark as viewed (defaults to current time)
 * @returns Confirmation of timestamp update
 */
export async function markNotificationsAsViewed(request: MarkNotificationsViewedRequest = {}): Promise<MarkNotificationsViewedResponse> {
  const endpoint = `/user/notifications`;
  const body = JSON.stringify({ action: 'markViewed', ...request });
  
  return fetchFromAPI<MarkNotificationsViewedResponse>(
    endpoint,
    (data) => data as MarkNotificationsViewedResponse,
    'POST',
    body,
    false
  );
}

// Notification Preferences types
export interface NotificationPreferences {
  activityEmails: boolean;
  monthlyUpdates: boolean;
}

export interface GetNotificationPreferencesResponse {
  preferences: NotificationPreferences;
  /** True when the address has stopped ALL marketing email — its own state, not a toggle value */
  globallySuppressed: boolean;
}

export interface UpdateNotificationPreferencesResponse {
  success: boolean;
  preferences: NotificationPreferences;
  message: string;
}

/**
 * Get user notification preferences
 * @returns Current notification preferences
 */
export async function getNotificationPreferences(): Promise<GetNotificationPreferencesResponse> {
  const endpoint = `/user/notifications`;
  const body = JSON.stringify({ action: 'getPreferences' });

  return fetchFromAPI<GetNotificationPreferencesResponse>(
    endpoint,
    (data) => data as GetNotificationPreferencesResponse,
    'POST',
    body,
    false
  );
}

/**
 * Update user notification preferences
 * @param preferences Partial preferences to update
 * @returns Updated preferences
 */
export async function updateNotificationPreferences(
  preferences: Partial<NotificationPreferences>
): Promise<UpdateNotificationPreferencesResponse> {
  const endpoint = `/user/notifications`;
  const body = JSON.stringify({ action: 'updatePreferences', preferences });

  return fetchFromAPI<UpdateNotificationPreferencesResponse>(
    endpoint,
    (data) => data as UpdateNotificationPreferencesResponse,
    'POST',
    body,
    false
  );
}

/**
 * Resume all marketing email: clears the global stop-all for the signed-in user's address.
 * Per-topic toggles keep their own state.
 */
export async function resumeMarketingEmail(): Promise<{ success: boolean; message: string }> {
  const endpoint = `/user/notifications`;
  const body = JSON.stringify({ action: 'resumeMarketingEmail' });

  return fetchFromAPI<{ success: boolean; message: string }>(
    endpoint,
    (data) => data as { success: boolean; message: string },
    'POST',
    body,
    false
  );
}

// ------------------------------------
// Community Functions
// ------------------------------------

export async function getCommunity(communityId: string): Promise<CommunityDisplayData> {
  const endpoint = `/community`;
  const body = JSON.stringify({ action: 'get', communityId });
  return fetchFromAPI<CommunityDisplayData>(
    endpoint,
    (data) => data as CommunityDisplayData,
    'POST',
    body,
    false
  );
}

export async function listCommunities(limit?: number, cursor?: string): Promise<{ communities: Community[]; cursor?: string }> {
  const endpoint = `/community`;
  const requestBody: { action: string; limit?: number; cursor?: string } = { action: 'list' };
  if (limit) requestBody.limit = limit;
  if (cursor) requestBody.cursor = cursor;
  const body = JSON.stringify(requestBody);
  return fetchFromAPI<{ communities: Community[]; cursor?: string }>(
    endpoint,
    (data) => data as { communities: Community[]; cursor?: string },
    'POST',
    body,
    false
  );
}

export async function getCommunityMembers(
  communityId: string,
  limit?: number,
  cursor?: string
): Promise<PaginatedResponse<CommunityMember>> {
  const endpoint = `/community`;
  const requestBody: { action: string; communityId: string; limit?: number; cursor?: string } = {
    action: 'getMembers',
    communityId
  };
  if (limit) requestBody.limit = limit;
  if (cursor) requestBody.cursor = cursor;
  const body = JSON.stringify(requestBody);
  return fetchFromAPI<PaginatedResponse<CommunityMember>>(
    endpoint,
    (data) => data as PaginatedResponse<CommunityMember>,
    'POST',
    body,
    false
  );
}

export async function getCommunityArtists(
  communityId: string,
  limit?: number,
  cursor?: string
): Promise<PaginatedResponse<CommunityArtist>> {
  const endpoint = `/community`;
  const requestBody: { action: string; communityId: string; limit?: number; cursor?: string } = {
    action: 'getArtists',
    communityId
  };
  if (limit) requestBody.limit = limit;
  if (cursor) requestBody.cursor = cursor;
  const body = JSON.stringify(requestBody);
  return fetchFromAPI<PaginatedResponse<CommunityArtist>>(
    endpoint,
    (data) => data as PaginatedResponse<CommunityArtist>,
    'POST',
    body,
    false
  );
}

export async function getCommunityFeed(
  communityId: string,
  limit?: number,
  cursor?: string
): Promise<{ items: ContentItem[]; cursor?: string }> {
  const endpoint = `/community`;
  const requestBody: { action: string; communityId: string; limit?: number; cursor?: string } = {
    action: 'getFeed',
    communityId
  };
  if (limit) requestBody.limit = limit;
  if (cursor) requestBody.cursor = cursor;
  const body = JSON.stringify(requestBody);
  return fetchFromAPI<{ items: ContentItem[]; cursor?: string }>(
    endpoint,
    (data) => data as { items: ContentItem[]; cursor?: string },
    'POST',
    body,
    false
  );
}

export async function getCommunityDiscussions(communityId: string): Promise<CommunityDiscussionsSnapshot> {
  const endpoint = `/community`;
  const body = JSON.stringify({ action: 'getDiscussions', communityId });
  return fetchFromAPI<CommunityDiscussionsSnapshot>(
    endpoint,
    (data) => data as CommunityDiscussionsSnapshot,
    'POST',
    body,
    false
  );
}

export async function getCommunityStats(communityId: string): Promise<{
  memberCount: number;
  artistCount: number;
  coralCount: number;
  totalMoneyFlowing: number;
}> {
  const endpoint = `/community`;
  const body = JSON.stringify({ action: 'getStats', communityId });
  return fetchFromAPI<{
    memberCount: number;
    artistCount: number;
    coralCount: number;
    totalMoneyFlowing: number;
  }>(
    endpoint,
    (data) => data as { memberCount: number; artistCount: number; coralCount: number; totalMoneyFlowing: number },
    'POST',
    body,
    false
  );
}

export async function getDashboardCard(): Promise<DashboardCardData> {
  const endpoint = `/community`;
  const body = JSON.stringify({ action: 'getDashboardCard' });
  return fetchFromAPI<DashboardCardData>(
    endpoint,
    (data) => data as DashboardCardData,
    'POST',
    body,
    false
  );
}